import type { CreativeDnDMovePayload, CreativeDnDZone } from '../../types/hypothesis';

export interface CreativeDragPreviewSource {
  currentTarget: HTMLElement;
  clientX: number;
  clientY: number;
  dataTransfer: DataTransfer;
}

export const HYPOTHESIS_CREATIVE_DRAG_MIME = 'application/x-hypothesis-creative';

export interface HypothesisCreativeDragData {
  creativeId: string;
  from: CreativeDnDZone & { index: number };
}

export const serializeHypothesisCreativeDrag = (
  data: HypothesisCreativeDragData,
): string => JSON.stringify(data);

export const parseHypothesisCreativeDrag = (
  dataTransfer: DataTransfer,
): HypothesisCreativeDragData | null => {
  const raw = dataTransfer.getData(HYPOTHESIS_CREATIVE_DRAG_MIME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as HypothesisCreativeDragData;
    if (
      !parsed.from ||
      (parsed.from.kind !== 'pack' && parsed.from.kind !== 'unpacked')
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const resolveDropIndex = (
  clientY: number,
  container: HTMLElement | null,
): number => {
  if (!container) return 0;
  const rows = container.querySelectorAll('.MuiDataGrid-row');
  if (rows.length === 0) return 0;

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows.item(i);
    const rect = row.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    if (clientY < midY) return i;
  }

  return rows.length;
};

export const buildMovePayload = (
  drag: HypothesisCreativeDragData,
  to: CreativeDnDZone & { index: number },
): CreativeDnDMovePayload => ({
  creativeId: drag.creativeId,
  from: drag.from,
  to,
});

const syncGhostRowLayout = (sourceRow: HTMLElement, ghostRow: HTMLElement): void => {
  const sourceCells = sourceRow.querySelectorAll('.MuiDataGrid-cell');
  const ghostCells = ghostRow.querySelectorAll('.MuiDataGrid-cell');
  const cellCount = Math.min(sourceCells.length, ghostCells.length);

  const rowStyle = window.getComputedStyle(sourceRow);
  ghostRow.style.display = rowStyle.display;
  ghostRow.style.flexDirection = rowStyle.flexDirection;
  ghostRow.style.alignItems = rowStyle.alignItems;
  ghostRow.style.boxSizing = rowStyle.boxSizing;
  ghostRow.style.transform = 'none';

  for (let i = 0; i < cellCount; i += 1) {
    const sourceCell = sourceCells.item(i);
    const ghostCell = ghostCells.item(i);
    if (!(sourceCell instanceof HTMLElement) || !(ghostCell instanceof HTMLElement)) {
      continue;
    }

    const cellStyle = window.getComputedStyle(sourceCell);
    const cellWidth = sourceCell.getBoundingClientRect().width;

    ghostCell.style.width = `${String(cellWidth)}px`;
    ghostCell.style.minWidth = `${String(cellWidth)}px`;
    ghostCell.style.maxWidth = `${String(cellWidth)}px`;
    ghostCell.style.flex = `0 0 ${String(cellWidth)}px`;
    ghostCell.style.flexGrow = cellStyle.flexGrow;
    ghostCell.style.flexShrink = cellStyle.flexShrink;
    ghostCell.style.boxSizing = cellStyle.boxSizing;
    ghostCell.style.padding = cellStyle.padding;
    ghostCell.style.display = cellStyle.display;
    ghostCell.style.alignItems = cellStyle.alignItems;
    ghostCell.style.justifyContent = cellStyle.justifyContent;
  }
};

/** Clones the DataGrid row and follows the cursor */
export const attachCreativeDragPreview = (
  event: CreativeDragPreviewSource,
  onEnd?: () => void,
): void => {
  const handle = event.currentTarget;
  const row = handle.closest('.MuiDataGrid-row');
  if (!(row instanceof HTMLElement)) return;

  const rect = row.getBoundingClientRect();
  const offsetX = event.clientX - rect.left;
  const offsetY = event.clientY - rect.top;

  const ghost = row.cloneNode(true) as HTMLElement;
  ghost.setAttribute('aria-hidden', 'true');
  syncGhostRowLayout(row, ghost);

  Object.assign(ghost.style, {
    position: 'fixed',
    top: `${String(rect.top)}px`,
    left: `${String(rect.left)}px`,
    width: `85%`,
    height: `${String(rect.height)}px`,
    pointerEvents: 'none',
    zIndex: '9999',
    opacity: '0.95',
    backgroundColor: '#fff',
    boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
    borderRadius: '4px',
    overflow: 'hidden',
  });
  document.body.appendChild(ghost);

  const onDrag = (e: globalThis.DragEvent) => {
    if (e.clientX === 0 && e.clientY === 0) return;
    ghost.style.left = `${String(e.clientX - offsetX)}px`;
    ghost.style.top = `${String(e.clientY - offsetY)}px`;
  };

  const cleanup = () => {
    document.removeEventListener('drag', onDrag);
    ghost.remove();
    onEnd?.();
  };

  document.addEventListener('drag', onDrag);
  handle.addEventListener('dragend', cleanup, { once: true });
};
